import { z } from "zod";

import { AccidentDynamicsEnum, GuiltDegreeEnum, LiabilityAnalysisSchema } from "../case";
import type { SystemTaskKey } from "./system-task-key";

export const LiabilityDeclineReasonSchema = z
  .enum([
    "prior_agreement_or_payment",
    "no_collection_standing",
    "no_causal_link",
    "concurrent_fault_uncollectible",
    "claimant_sole_fault",
    "inconclusive_accident_dynamics",
    "insufficient_documentation",
    "unproven_damages_or_amounts",
    "time_barred",
    "third_party_liability_unproven",
    "responsible_party_unidentified",
    "other",
  ])
  .describe("Every reason a stored decline may carry, including retired ones");

export const SelectableLiabilityDeclineReasonSchema = LiabilityDeclineReasonSchema.exclude([
  "no_collection_standing",
  "no_causal_link",
]).describe("Reasons a new liability decline may record");
export type SelectableLiabilityDeclineReason = z.infer<
  typeof SelectableLiabilityDeclineReasonSchema
>;

export const LiabilityReviewSnapshotSchema = z.object({
  accidentDynamics: AccidentDynamicsEnum.optional(),
  guiltDegree: GuiltDegreeEnum.optional(),
});
export type LiabilityReviewSnapshot = z.infer<typeof LiabilityReviewSnapshotSchema>;

export const requiresLiabilityDeclineReason = (
  system: SystemTaskKey | null,
  solution: string,
): boolean => system === "analyze_case_liability" && solution === "close_process";

export const reviewedLiabilityValuesFromCase = (
  parent: Record<string, unknown> | null | undefined,
): LiabilityReviewSnapshot | undefined => {
  if (parent?.caseType !== "third_party_claim") return undefined;
  const analysis = LiabilityAnalysisSchema.safeParse(parent.liabilityAnalysis);
  if (!analysis.success) return undefined;

  return {
    accidentDynamics: analysis.data.accidentDynamics?.reviewed,
    guiltDegree: analysis.data.guiltDegree?.reviewed,
  };
};
